import React, {
    useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';

import DropdownTreeSelect from 'react-dropdown-tree-select';
import 'react-dropdown-tree-select/dist/styles.css';
import {
    FormControl, FormControlLabel, FormHelperText, IconButton, Switch, Typography
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import withStyles from '@mui/styles/withStyles';
import classNames from 'classnames/bind';
import { v4 as uuid } from 'uuid';
import { red } from '@mui/material/colors';
import { useTranslation } from 'react-i18next';
import { COLORS } from '../../../utils';
import InfoTooltip from '../InfoTooltip';

const styles = () => ({
    root: {
        paddingTop: 14,
        '& .react-dropdown-tree-select .node.blocked label, & .react-dropdown-tree-select .node.blocked .checkbox-item': {
            pointerEvents: 'none',
            opacity: 0.5,
        },
    },
    noToggler: {
        '& .dropdown-trigger.arrow.top:after': {
            display: 'none',
        },
    },
    treeSelectCustomWrapper: {
        display: 'flex',
        flexDirection: 'row',
    },
    treeSelectCustom: {
        width: 'calc(100% - 30px)'
    },
    deleteIcon: {
        position: 'flex',
        padding: '5px'
    },
    placeholder: {
        position: 'absolute',
        left: 0,
        top: 12,
        fontSize: 16,
        cursor: 'text',
        transition: 'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms,transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms'
    },
    onFocus: {
        transform: 'translate(0, 1.5px) scale(0.75)',
        transformOrigin: 'top left',
        cursor: 'default',
        top: -10
    },
    strictContainer: {
        width: '100%',
        display: 'flex',
        alignItems: 'flex-end',
        flexDirection: 'row-reverse',
        justifyContent: 'space-between'
    },
    treeSelectContainer: {
        width: '100%'
    },
    marginStates: {
        marginLeft: '15px'
    },
    blocked: {
        '&.tag-item .tag': {
            backgroundColor: COLORS.lightRedColor,
            borderColor: COLORS.lightRedColor,
            color: COLORS.whiteGreyColor,
            '&:hover': {
                boxShadow: `0px 0px 10px 1px ${red[700]} inset`,
                '& button': {
                    color: 'white'
                }
            }
        },
        '&.tag-item .tag-remove': {
            color: `${COLORS.whiteGreyColor} !important`,
            pointerEvents: 'auto',
            opacity: 1,
            cursor: 'pointer',
        },
    },
    tag: {
        '& .tag:hover': {
            boxShadow: `0 0 10px 1px ${COLORS.darkGrayColor} inset`,
        },
        '& .react-dropdown-tree-select .tag-item:hover .tag-remove': {
            color: 'black',
        },
    },
    isFirm: {
        '&.node > label > .node-label': {
            fontWeight: 700,
            color: COLORS.accent.main
        },
    }
});

const RESOURCE_OWNER = 'resourceOwner';
const WHEREABOUTS = 'whereabouts';

function Placeholder(props) {
    const {
        focus,
        content,
        error,
        classes: { placeholder }
    } = props;

    const classFocusOn = {
        [props.classes.onFocus]: focus || content.length
    };

    return (
        <Typography
            color={(() => {
                if (!error && focus) {
                    return 'primary';
                }
                if (!error && !focus) {
                    return 'textSecondary';
                }
                if (error) {
                    return 'error';
                }
            })()}
            className={classNames(placeholder, classFocusOn)}
            component="label"
            htmlFor={props.htmlFor}
            {...props.innerProps}
        >
            {props.children}
        </Typography>
    );
}

Placeholder.propTypes = {
    focus: PropTypes.bool,
    content: PropTypes.array,
    htmlFor: PropTypes.string,
    innerProps: PropTypes.object,
    children: PropTypes.node,
    error: PropTypes.string,
    classes: PropTypes.shape({
        onFocus: PropTypes.string,
        placeholder: PropTypes.string,
    }),
};

function equalArrays(a, b) {
    if (a.length !== b.length) return false;

    for (let i = 0; i < a.length; i++)
        if (a[i] !== b[i]) return false;

    return true;
}

function markSelectedFields(locations, selectedNodes, inh, classes, placeholder) {
    const resultArr = [];
    const additionalArr = [];
    let callback = data => data;
    const renameFields = (location, isParentTrue, parentLoc = {}) => {

        const isResourceOwner = placeholder === RESOURCE_OWNER;
        const isResourceOwnerWithoutNodeType = isResourceOwner && !location.nodeType;
        const isNotFirm = isResourceOwner && location.nodeType !== 3;

        if (selectedNodes.includes(location.value) || (isParentTrue && !inh)) {
            location.checked = true;
            location.expanded = true;
            location.className = [
                (isResourceOwnerWithoutNodeType || isNotFirm) && 'blocked',
                isResourceOwner && location.nodeType === 3 && classes.isFirm,
            ].filter(Boolean)
                .join(' ');

            location.tagClassName = !location.isActive || (isResourceOwnerWithoutNodeType || isNotFirm) ?
                classes?.blocked : classes?.tag;

            callback = data => {
                parentLoc.expanded = true;
                return data;
            };
            resultArr.push(location.value);
            if (!selectedNodes.includes(location.value) || (isParentTrue && !inh)) {
                additionalArr.push(location.value);
            }
        } else {
            location.className = [
                (isResourceOwnerWithoutNodeType || isNotFirm) && 'blocked',
                isResourceOwner && location.nodeType === 3 && classes.isFirm,
            ].filter(Boolean)
                .join(' ');

            location.checked = false;
            location.expanded = false;
        }
        location.children.map(item => callback(renameFields(item, location.checked, location)));

        return location;
    };

    return [locations.map(i => renameFields(i)), resultArr, additionalArr];
}

const useComponentWillMount = func => useMemo(func, []);

const useComponentDidMount = func => useEffect(func, []);

const assignObjectPaths = (obj, stack) => {
    Object.keys(obj).forEach(k => {
        const node = obj[k];
        if (typeof node === 'object') {
            node.path = stack ? `${stack}.${k}` : k;
            assignObjectPaths(node, node.path);
        }
    });
};

const LocationTreeModeTypeahead = props => {
    const {
        classes,
        data,
        placeholder,
        locationId,
        hierarchical,
        multi,
        setLocation,
        error,
        required,
        dropdownOutside,
        descendants,
        strict = false,
        setStrict = e => e,
        onChangeStatusButton,
        isLicense,
        isOldLocation,
        tooltip,
        tooltipDescriptionId,
        titleId,
        immutableData,
    } = props;

    const { t } = useTranslation();
    const [localValue, setLocalValue] = useState([]);
    const myRef = useRef(null);
    const [isOnFocus, setFocusValue] = useState(false);
    const [ids, setIds] = useState({ treeSelectID: '', inputID: '' });
    const [inh, setInh] = useState(strict || false);
    const [prevInh, setPrevInh] = useState(inh);
    const { treeSelectID, inputID } = ids;
    const [treeData, setTreeData] = useState(JSON.parse(JSON.stringify(data)));
    const resultArrR = useRef(localValue);
    const additionalArrR = useRef([]);

    const [isHover, setIsHover] = useState(true);
    const handleMouseOver = () => setIsHover(true);
    const handleMouseLeave = () => setIsHover(true);

    useEffect(() => {
        setLocalValue(locationId);
    }, [locationId]);

    useEffect(() => {
        const [newTreeData, resultArr, additionalArr] =
            markSelectedFields(treeData, localValue, inh, classes, placeholder);
        setTreeData(newTreeData);

        if (!equalArrays(resultArrR.current, resultArr) || prevInh !== inh) {
            setPrevInh(inh);
            resultArrR.current = !inh ?
                resultArr :
                (() => resultArr.map(item => additionalArrR.current.includes(item) ? null : item).filter(n => n)
                )();
            additionalArrR.current = additionalArr;
            markSelectedFields(treeData, resultArrR.current, inh);
            setLocation(resultArrR.current);
            setLocalValue(resultArrR.current);
        }

        if (strict !== inh) {
            setInh(strict);
        }
    }, [inh, localValue, prevInh, setLocation, strict]);

    useEffect(() => {
        if (!immutableData) {
            setTreeData(JSON.parse(JSON.stringify(data)));
        }
    }, [data]);

    useComponentWillMount(() => {
        const random = uuid();
        setIds({
            treeSelectID: `tree-select_${random}`,
            inputID: `tree-select_${random}-input`
        });
    });

    useComponentDidMount(() => {
        document.querySelector(`#${treeSelectID}`).querySelector('.search').id = inputID;
    });

    if (treeData.length === 1 && treeData[0].hasOwnProperty('expanded')) {
        treeData[0].expanded = true;
    }

    assignObjectPaths(treeData);

    return (
        <div className={props.classes.nonStrictContainer}>
            {((hierarchical && placeholder !== RESOURCE_OWNER) &&
                (hierarchical && placeholder !== WHEREABOUTS)) && (

                <FormControl>
                    <FormControlLabel
                        className={!isLicense ? classes.marginStates : ''}
                        control={(
                            <Switch
                                onChange={e => {
                                    e.persist();
                                    setInh(prev => {
                                        setPrevInh(prev);
                                        return e.target.checked;
                                    });
                                    setStrict(e.target.checked);
                                    document.querySelector(`#${inputID}`).focus();
                                }}
                                checked={inh}
                            />
                        )}
                        label={t('strict')}
                    />
                </FormControl>
            )}
            <FormControl
                className={props.classes.treeSelectContainer}
                onMouseOver={handleMouseOver}
                onMouseLeave={handleMouseLeave}
            >
                <div className={props.classes.root}>
                    <div className={classes.treeSelectCustomWrapper}>
                        <DropdownTreeSelect
                            id={treeSelectID}
                            data={treeData}
                            className={
                                `${classes.treeSelectCustom} \n` +
                                `${classes.noToggler} \n` +
                                `tree-select-custom\n` +
                                `${props.error ? ' throw-error' : ''}`
                            }
                            texts={{
                                placeholder: ' '
                            }}
                            keepChildrenOnSearch
                            keepTreeOnSearch
                            searchPredicate={(node, searchTerm) =>
                                node.label && node.label.toLowerCase().indexOf(searchTerm) >= 0}
                            mode={inh ? 'hierarchical' : multi ? 'multiSelect' : 'radioSelect'}
                            onChange={(currentNode, selectedNodes) => {
                                if (multi) {
                                    setLocalValue(selectedNodes.flatMap(item => {
                                        if (descendants && !inh) { // FIXME
                                            return item.value;
                                        }
                                        return item.value;
                                    }));
                                    if (!currentNode.checked && !isOnFocus) {
                                        setLocation(localValue);
                                    }
                                    if (onChangeStatusButton) {
                                        onChangeStatusButton();
                                    }
                                } else {
                                    if (!currentNode.checked) {
                                        setLocation([]);
                                        return;
                                    }
                                    if (selectedNodes.length) {
                                        const index = selectedNodes.indexOf(currentNode);
                                        setLocation([selectedNodes[index >= 0 ? index : 0].value]);
                                        setFocusValue(false);
                                    } else {
                                        setLocation([]);
                                    }
                                }
                            }}
                            onBlur={() => {
                                setFocusValue(false);
                            }}
                            onFocus={() => {
                                setFocusValue(true);
                                setTimeout(() => {
                                    const refNode = myRef.current?.node;
                                    if (!refNode) return;
                                    const dropDownNode = refNode.getElementsByClassName('dropdown-content')[0];
                                    dropDownNode.scrollIntoView({ behavior: 'smooth' });
                                    if (dropdownOutside) dropDownNode.style.cssText += 'position:fixed; box-sizing: border-box;';
                                }, 0);
                            }}
                            ref={myRef}
                        />

                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            {!!locationId.length && (
                                <IconButton
                                    className={classes.deleteIcon}
                                    aria-label="delete"
                                    size="small"
                                    onClick={e => {
                                        e.preventDefault();
                                        setLocation([]);
                                        if (onChangeStatusButton) {
                                            onChangeStatusButton();
                                        }
                                    }}
                                >
                                    <DeleteIcon />
                                </IconButton>
                            )}
                            {isOldLocation && tooltip && isHover && (
                                <InfoTooltip
                                    titleId={titleId}
                                    descriptionId={tooltipDescriptionId}
                                    handleMouseLeave={handleMouseLeave}
                                    attention
                                    t={t}
                                />
                            )}
                        </div>

                    </div>
                    {error && (
                        <FormHelperText
                            variant="standard"
                            error={!!error}
                        >
                            {t(error)}
                        </FormHelperText>
                    )}
                    <Placeholder
                        classes={classes}
                        htmlFor={inputID}
                        focus={isOnFocus}
                        content={localValue}
                        error={error}
                    >
                        {`${t(placeholder)} ${required ? '*' : ''}`}
                    </Placeholder>
                </div>
            </FormControl>
        </div>
    );
};

LocationTreeModeTypeahead.propTypes = {
    data: PropTypes.array.isRequired,
    placeholder: PropTypes.string.isRequired,
    error: PropTypes.string,
    multi: PropTypes.bool,
    descendants: PropTypes.bool,
    required: PropTypes.bool,
    dropdownOutside: PropTypes.bool,
    locationId: PropTypes.any,
    setLocation: PropTypes.func.isRequired,
    strict: PropTypes.bool,
    setStrict: PropTypes.func,
    onChangeStatusButton: PropTypes.func,
    classes: PropTypes.shape({
        root: PropTypes.string,
        onFocus: PropTypes.string,
        placeholder: PropTypes.string,
        noToggler: PropTypes.string,
        deleteIcon: PropTypes.string,
        treeSelectCustom: PropTypes.string,
        treeSelectCustomWrapper: PropTypes.string,
    }),
};

export default withStyles(styles)(LocationTreeModeTypeahead);
